/**
 * FlahaSOIL v2 — Reports navigation hub.
 *
 * Reports are generated per soil test; this page routes users through
 * projects to tests. Glass cards + EmptyState for dark workspace clarity.
 */
import {
	Alert,
	Box,
	ButtonBase,
	Chip,
	Stack,
	Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ProjectSummaryDTO } from "@flaha/shared-types";

import { useAuth } from "../auth";
import { EmptyState } from "../layouts/components/EmptyState";
import { GlassSurface } from "../layouts/components/GlassSurface";
import { usePageHeader } from "../layouts/PageHeaderContext";
import { getApiClient } from "../services/apiClientProvider";

export function ReportsPage() {
	const navigate = useNavigate();
	const { status: sessionStatus, activeOrganization } = useAuth();
	const [projects, setProjects] = useState<ProjectSummaryDTO[] | null>(null);
	const [error, setError] = useState<string | null>(null);

	usePageHeader({
		title: "Reports",
		subtitle: "Browse projects to open per-test reports",
		breadcrumbs: [
			{ label: "Home", to: "/" },
			{ label: "Dashboard", to: "/dashboard" },
			{ label: "Reports" },
		],
	});

	useEffect(() => {
		if (sessionStatus !== "authenticated") return;
		let cancelled = false;
		setError(null);
		setProjects(null);
		getApiClient()
			.listProjects({})
			.then((res) => {
				if (!cancelled) setProjects(res.projects);
			})
			.catch((err: unknown) => {
				if (!cancelled) {
					setError(err instanceof Error ? err.message : String(err));
				}
			});
		return () => {
			cancelled = true;
		};
	}, [sessionStatus, activeOrganization?.id]);

	return (
		<Box data-testid="reports-workspace">
			<Typography
				variant="body2"
				sx={{ mb: 3, color: "workspace.textSecondary", maxWidth: 720 }}
			>
				Reports are generated per soil test. Choose a project to browse its
				samples and tests, then open a test to create a versioned report or
				download PDF.
			</Typography>

			{error && (
				<Alert severity="error" sx={{ mb: 2 }}>
					Failed to load projects: {error}
				</Alert>
			)}

			{projects === null && !error && (
				<Typography sx={{ color: "workspace.textMuted" }}>Loading…</Typography>
			)}

			{projects !== null && projects.length === 0 && (
				<EmptyState
					icon={<AssessmentIcon />}
					title="No projects to report on yet"
					description="Create a project, add a soil test, then generate a versioned scientific report from the test results page."
					primaryAction={{ label: "Go to projects", to: "/projects" }}
					secondaryAction={{
						label: "Start soil analysis",
						to: "/soil-tests/new",
						variant: "outlined",
					}}
				/>
			)}

			{projects !== null && projects.length > 0 && (
				<Stack spacing={1.25}>
					{projects.map((p) => (
						<GlassSurface key={p.id} intensity="standard" depth="raised" sx={{ p: 0 }}>
							<ButtonBase
								onClick={() => navigate(`/projects/${p.id}`)}
								focusRipple
								sx={{
									display: "block",
									width: "100%",
									textAlign: "left",
									p: { xs: 1.75, sm: 2.25 },
									borderRadius: "inherit",
									color: "inherit",
									"&:focus-visible": {
										outline: (theme) =>
											`3px solid ${theme.palette.workspace.blue}`,
										outlineOffset: -3,
									},
								}}
							>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									alignItems={{ xs: "flex-start", sm: "center" }}
									spacing={1}
								>
									<Typography
										variant="subtitle1"
										fontWeight={700}
										sx={{ flexGrow: 1 }}
									>
										{p.name}
										{p.code ? ` · ${p.code}` : ""}
									</Typography>
									<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
										<Chip
											label={`${p.sampleCount} sample${p.sampleCount === 1 ? "" : "s"}`}
											size="small"
										/>
										<Chip
											label={p.status}
											size="small"
											color={p.status === "ACTIVE" ? "success" : "default"}
											variant="outlined"
										/>
										<Stack
											direction="row"
											alignItems="center"
											spacing={0.5}
											sx={{ color: "workspace.green", fontWeight: 600 }}
										>
											<Typography variant="body2" component="span">
												Open
											</Typography>
											<ArrowForwardIcon fontSize="small" aria-hidden />
										</Stack>
									</Stack>
								</Stack>
								<Typography
									variant="caption"
									sx={{ color: "workspace.textMuted", display: "block", mt: 1 }}
								>
									Open project → sample → soil test → generate or download report
								</Typography>
							</ButtonBase>
						</GlassSurface>
					))}
				</Stack>
			)}
		</Box>
	);
}
